import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  ExternalLink,
  ShieldCheck,
  Sparkles,
  Loader2,
  Theater,
  CheckCircle2,
  GraduationCap,
  PlayCircle,
  Phone,
} from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { PageHero } from "@/components/PageHero";
import { RelatedLinks } from "@/components/RelatedLinks";
import { usePageMeta } from "@/hooks/usePageMeta";
import { useCampaign } from "@/hooks/useCampaign";
import { SITE } from "@/data/site";
import { YOUTH_PATHWAY } from "@/data/youthPathway";

const JOTFORM_URL = process.env.REACT_APP_JOTFORM_YOUTH_URL || "";

export default function YouthTheatrePage() {
  const [loaded, setLoaded] = useState(false);
  useCampaign();

  usePageMeta(
    "USDD Youth Dance Theatre Company · Auditions & Pathway",
    "Join the USDD Youth Dance Theatre Company — performance-focused training, choreography development and live show opportunities for committed young dancers across London & Kent."
  );

  return (
    <>
      <Header />
      <main>
        <PageHero
          overline="Youth Dance Theatre Company · Pathway"
          title="Train. Create."
          highlight="Perform."
          description="The USDD Youth Dance Theatre Company is our performance pathway for committed young dancers — choreography development, stage craft, ensemble work and real production opportunities across London & Kent."
          ctaPrimary={{ label: "Apply now", to: "/youth-theatre?focus=youth&intent=apply#apply" }}
          ctaSecondary={{ label: "Register interest", to: "/contact?focus=youth&intent=enquiry" }}
          bg={SITE.kidsShow}
        />

        {/* Overview */}
        <section className="py-20 sm:py-24 bg-black" data-testid="youth-overview">
          <div className="max-w-7xl mx-auto px-6 sm:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-12 pb-8 border-b border-white/10">
              <div className="lg:col-span-7">
                <div className="flex items-center gap-3 mb-4">
                  <Theater size={18} className="text-brand-yellow" />
                  <span className="text-xs font-bold uppercase tracking-[0.3em] text-brand-yellow">
                    {YOUTH_PATHWAY.overview.eyebrow}
                  </span>
                </div>
                <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-white tracking-wide leading-[0.95]">
                  {YOUTH_PATHWAY.overview.title}{" "}
                  <span className="text-brand-yellow text-glow-yellow">
                    {YOUTH_PATHWAY.overview.highlight}
                  </span>
                </h2>
              </div>
              <div className="lg:col-span-5 lg:pt-4">
                <p className="text-zinc-300/90 text-base sm:text-lg leading-relaxed">
                  {YOUTH_PATHWAY.overview.body}
                </p>
                <ul className="mt-6 space-y-2.5">
                  {YOUTH_PATHWAY.overview.points.map((p) => (
                    <li key={p} className="flex items-start gap-2.5 text-sm text-zinc-300">
                      <CheckCircle2 size={16} className="text-brand-yellow mt-0.5 flex-shrink-0" />
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Audition process */}
        <section className="py-20 bg-gradient-to-b from-black via-zinc-950 to-black" data-testid="youth-audition">
          <div className="max-w-7xl mx-auto px-6 sm:px-8">
            <div className="flex items-center gap-3 mb-8">
              <span className="h-px w-10 bg-brand-yellow" />
              <span className="text-xs font-bold uppercase tracking-[0.3em] text-brand-yellow">
                Audition & Application Process
              </span>
            </div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-white tracking-wide leading-[0.95] mb-10">
              Four steps to <span className="text-brand-yellow">the stage</span>.
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {YOUTH_PATHWAY.audition.map((s, i) => (
                <motion.div
                  key={s.step}
                  data-testid={`youth-audition-step-${i}`}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.45, delay: i * 0.08 }}
                  className="relative p-6 rounded-2xl bg-white/[0.03] border border-white/10 hover:border-brand-yellow/40 transition-all overflow-hidden"
                >
                  <div className="absolute -top-10 -right-10 w-32 h-32 rounded-full bg-brand-yellow/8 blur-2xl pointer-events-none" />
                  <div className="font-display text-5xl text-brand-yellow tracking-wide leading-none">
                    {s.step}
                  </div>
                  <h3 className="mt-4 font-display text-xl text-white tracking-wide">{s.title}</h3>
                  <p className="mt-2 text-xs text-zinc-400 leading-relaxed">{s.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Rehearsal expectations */}
        <section className="py-20 bg-black" data-testid="youth-rehearsals">
          <div className="max-w-7xl mx-auto px-6 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-5">
              <div className="flex items-center gap-3 mb-4">
                <span className="h-px w-10 bg-brand-yellow" />
                <span className="text-xs font-bold uppercase tracking-[0.3em] text-brand-yellow">
                  {YOUTH_PATHWAY.rehearsals.eyebrow}
                </span>
              </div>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-white tracking-wide leading-[0.95]">
                {YOUTH_PATHWAY.rehearsals.title}
              </h2>
              <p className="mt-5 text-sm sm:text-base text-zinc-400 leading-relaxed max-w-md">
                {YOUTH_PATHWAY.rehearsals.note}
              </p>
            </div>
            <div className="lg:col-span-7">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {YOUTH_PATHWAY.rehearsals.items.map((it, i) => (
                  <div
                    key={it.label}
                    data-testid={`youth-rehearsal-${i}`}
                    className="p-5 rounded-2xl bg-gradient-to-br from-brand-purpleDeep/30 to-zinc-950 border border-white/10"
                  >
                    <it.icon className="text-brand-yellow mb-3" size={22} />
                    <div className="text-[10px] uppercase tracking-[0.28em] text-zinc-400">{it.label}</div>
                    <div className="font-display text-xl text-white tracking-wide mt-1">{it.value}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Performance opportunities */}
        <section className="py-20 bg-gradient-to-b from-black via-zinc-950 to-black" data-testid="youth-performance">
          <div className="max-w-7xl mx-auto px-6 sm:px-8">
            <div className="flex items-center gap-3 mb-8">
              <PlayCircle size={18} className="text-brand-yellow" />
              <span className="text-xs font-bold uppercase tracking-[0.3em] text-brand-yellow">
                Performance Opportunities
              </span>
            </div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-white tracking-wide leading-[0.95] mb-10 max-w-3xl">
              Real stages. <span className="text-brand-yellow text-glow-yellow">Real audiences.</span>
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {YOUTH_PATHWAY.performance.map((p, i) => (
                <motion.div
                  key={p.title}
                  data-testid={`youth-performance-${i}`}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.45, delay: i * 0.08 }}
                  className="relative p-6 rounded-2xl bg-white/[0.03] border border-white/10 hover:border-brand-yellow/50 transition-all"
                >
                  <span className="text-[10px] uppercase tracking-[0.28em] text-brand-yellow font-bold">{p.tag}</span>
                  <h3 className="mt-3 font-display text-2xl text-white tracking-wide leading-tight">{p.title}</h3>
                  <p className="mt-2 text-sm text-zinc-400 leading-relaxed">{p.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Progression pathway */}
        <section className="py-20 bg-black" data-testid="youth-progression">
          <div className="max-w-5xl mx-auto px-6 sm:px-8">
            <div className="flex items-center gap-3 mb-6">
              <GraduationCap size={18} className="text-brand-yellow" />
              <span className="text-xs font-bold uppercase tracking-[0.3em] text-brand-yellow">
                Pathway into productions & qualifications
              </span>
            </div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-white tracking-wide leading-[0.95] mb-10">
              From company to <span className="text-brand-yellow">career</span>.
            </h2>
            <div className="relative p-7 sm:p-10 rounded-3xl bg-gradient-to-br from-brand-purple/30 via-brand-purpleDeep/30 to-black border border-brand-purpleLight/30 backdrop-blur-xl">
              <div className="absolute -top-12 -left-12 w-56 h-56 rounded-full bg-brand-yellow/10 blur-3xl pointer-events-none" />
              <div className="relative space-y-5">
                {YOUTH_PATHWAY.progression.map((s, i) => (
                  <motion.div
                    key={s.code}
                    data-testid={`youth-progression-${i}`}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: i * 0.08 }}
                    className="flex items-start gap-4 sm:gap-6 pb-5 border-b border-white/10 last:border-b-0 last:pb-0"
                  >
                    <div className="font-display font-black text-brand-yellow w-28 sm:w-40 text-sm sm:text-base uppercase tracking-[0.2em] flex-shrink-0">
                      {s.code}
                    </div>
                    <div className="flex-1">
                      <div className="font-display text-lg sm:text-xl text-white tracking-wide leading-tight">{s.label}</div>
                      <div className="text-xs sm:text-sm text-zinc-400 mt-1">{s.desc}</div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Application form */}
        <section id="apply" data-focus="youth" data-testid="youth-apply" className="py-20 bg-gradient-to-b from-black via-zinc-950 to-black scroll-mt-24">
          <div className="max-w-5xl mx-auto px-6 sm:px-8">
            <div className="flex items-center gap-3 mb-6">
              <Sparkles size={16} className="text-brand-yellow" />
              <span className="text-[10px] font-bold uppercase tracking-[0.3em] text-brand-yellow">
                Company Application
              </span>
            </div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-white tracking-wide leading-[0.95] mb-8">
              Apply for the <span className="text-brand-yellow text-glow-yellow">2026 season</span>.
            </h2>

            {JOTFORM_URL ? (
              <div className="relative rounded-3xl border border-white/10 overflow-hidden bg-white">
                {!loaded && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/80 z-10">
                    <Loader2 className="text-brand-yellow animate-spin" size={28} />
                  </div>
                )}
                <iframe
                  data-testid="youth-jotform-iframe"
                  src={JOTFORM_URL}
                  title="USDD Youth Dance Theatre Company application"
                  width="100%"
                  height="720"
                  frameBorder="0"
                  onLoad={() => setLoaded(true)}
                  className="w-full"
                  style={{ minHeight: "720px", background: "white" }}
                />
                <div className="absolute top-3 right-3 z-20">
                  <a
                    href={JOTFORM_URL}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-black/70 backdrop-blur border border-white/15 text-[10px] uppercase tracking-[0.25em] text-zinc-300 hover:text-brand-yellow"
                  >
                    Open form <ExternalLink size={11} />
                  </a>
                </div>
              </div>
            ) : (
              <div className="rounded-3xl border border-brand-yellow/30 bg-gradient-to-br from-brand-purpleDeep/40 to-zinc-950 p-8 sm:p-12">
                <div className="text-[10px] uppercase tracking-[0.3em] text-brand-yellow mb-2">
                  Application form connecting soon
                </div>
                <h3 className="font-display text-3xl text-white tracking-wide">
                  We're wiring up the form.
                </h3>
                <p className="mt-3 text-sm text-zinc-400 max-w-xl">
                  Message us and we'll send you the application directly within 24 hours.
                </p>
              </div>
            )}

            <div className="mt-6 flex items-center justify-center gap-2 text-[10px] uppercase tracking-[0.25em] text-zinc-500">
              <ShieldCheck size={12} /> Your data is kept private. We never share applications.
            </div>
          </div>
        </section>

        {/* Final CTA strip */}
        <section className="py-16 bg-black">
          <div className="max-w-5xl mx-auto px-6 sm:px-8">
            <div className="relative overflow-hidden rounded-3xl border border-brand-yellow/30 bg-gradient-to-br from-brand-purpleDeep via-black to-black p-7 sm:p-10">
              <div className="absolute -top-20 -right-20 w-[300px] h-[300px] rounded-full bg-brand-yellow/15 blur-[100px]" />
              <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-6 items-center">
                <div className="lg:col-span-7">
                  <div className="text-[10px] uppercase tracking-[0.3em] text-brand-yellow mb-3">Three ways to start</div>
                  <h3 className="font-display text-3xl sm:text-4xl text-white tracking-wide leading-[0.95]">
                    Apply, register interest <span className="text-brand-yellow">or talk to us</span>.
                  </h3>
                </div>
                <div className="lg:col-span-5 flex flex-col gap-3">
                  <Link
                    to="/youth-theatre#apply"
                    data-testid="youth-cta-apply"
                    className="inline-flex items-center justify-between gap-3 px-6 py-3.5 rounded-full bg-brand-yellow text-black font-black uppercase text-sm tracking-wider hover:bg-brand-yellowHover hover:scale-[1.02] transition-all glow-yellow"
                  >
                    Apply now <ArrowRight size={16} />
                  </Link>
                  <Link
                    to="/contact?focus=youth&intent=enquiry"
                    data-testid="youth-cta-register-interest"
                    className="inline-flex items-center justify-between gap-3 px-6 py-3.5 rounded-full border border-white/25 bg-white/5 backdrop-blur text-white font-bold uppercase text-sm tracking-wider hover:bg-white hover:text-black hover:border-white transition-all"
                  >
                    Register interest <ArrowRight size={16} />
                  </Link>
                  <a
                    href={`tel:${SITE.contact.phone.replace(/\s/g, "")}`}
                    data-testid="youth-cta-call"
                    className="inline-flex items-center justify-between gap-3 px-6 py-3.5 rounded-full border border-white/25 bg-white/5 backdrop-blur text-white font-bold uppercase text-sm tracking-wider hover:bg-white hover:text-black hover:border-white transition-all"
                  >
                    <span className="inline-flex items-center gap-2"><Phone size={15} /> Talk to us</span>
                    <span className="text-xs opacity-70">{SITE.contact.phone}</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        <RelatedLinks
          title="Related programmes"
          items={[
            { tag: "Kids", label: "USDD Kids", desc: "Ages 6–17 weekly programme", to: "/kids" },
            { tag: "Registration", label: "Kids Registration", desc: "Full enrolment or £5 Kids Taster", to: "/kids-registration" },
            { tag: "Events", label: "Showcases & Battles", desc: "Live performance opportunities", to: "/events" },
          ]}
        />
      </main>
      <Footer />
    </>
  );
}
